import { ref, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useUserStore } from "@/core/store/useUserStore";
import { supabase } from "../core/supabase";
import { useAuthAnalytics } from "@/core/analytics/hooks/useAuthAnalytics";
import { usePostAuthSessionHandler } from "@/composables/usePostAuthSessionHandler";
import {
  authRuntime,
  extensionRuntime,
  runtimeEnv,
} from "@/config/featureFlags";
import { resolveRedirectTarget } from "@/core/auth/authRedirect";

const GOOGLE_GSI_SCRIPT_ID = "google-gsi-client";
const GOOGLE_GSI_SRC = "https://accounts.google.com/gsi/client";

/**
 * Google One Tap 共享 composable（方案 §13.x）。
 *
 * 从 LoginView 抽离，供 Login 与 Landing 复用同一套初始化 / 回调 / 埋点逻辑，
 * 避免两份内联实现漂移。所有埋点 sourcePosition 由调用方通过 options 注入：
 * - Login 页传 "login_page"
 * - Landing 页传 "landing_page"
 *
 * @param {Object} options
 * @param {string} [options.sourcePosition="login_page"] 埋点来源位
 */
export function useGoogleOneTap(options = {}) {
  const sourcePosition = options.sourcePosition || "login_page";
  const router = useRouter();
  const userStore = useUserStore();
  const authAnalytics = useAuthAnalytics();
  const { completePostAuthSession } = usePostAuthSessionHandler();

  // 组件是否已卸载（方案 §13.6，per-instance ref）：卸载后的 stale auth promise 不得再导航 / 触发 billing
  const isUnmounted = ref(false);
  // 登录是否已成功（凭据校验通过）：成功跳转导致的卸载不再调用 cancel()，
  // 避免浮层随导航被代码关闭时回发多余的 auth_one_tap_prompt_completed(skipped/cancel_called)。
  const loginSucceeded = ref(false);
  let oneTapInitialized = false;
  // OAuth 单飞标志（方案 §4.6 / §13.2）：首次 provider 请求 pending 期间拦截到达的 One Tap credential。
  // 通过 setOauthInFlight / getOauthInFlight 暴露给同页手动 OAuth 流程共享（Login 页使用）。
  let oauthInFlight = false;
  let googleOneTapRawNonce = null;

  const loadGoogleGsiSdk = () => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return Promise.reject(new Error("Browser environment unavailable"));
    }

    if (window.google?.accounts?.id) {
      return Promise.resolve(window.google.accounts.id);
    }

    return new Promise((resolve, reject) => {
      const existingScript = document.getElementById(GOOGLE_GSI_SCRIPT_ID);

      if (existingScript) {
        existingScript.addEventListener(
          "load",
          () => resolve(window.google?.accounts?.id),
          { once: true }
        );
        existingScript.addEventListener("error", reject, { once: true });
        return;
      }

      const script = document.createElement("script");
      script.id = GOOGLE_GSI_SCRIPT_ID;
      script.src = GOOGLE_GSI_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(window.google?.accounts?.id);
      script.onerror = reject;
      document.head.appendChild(script);
    });
  };

  const createGoogleOneTapRawNonce = () => {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
      ""
    );
  };

  const createSha256Hex = async (value) => {
    const bytes = new TextEncoder().encode(value);
    const digest = await window.crypto.subtle.digest("SHA-256", bytes);
    return Array.from(new Uint8Array(digest), (byte) =>
      byte.toString(16).padStart(2, "0")
    ).join("");
  };

  const handleGoogleOneTapCredential = async (response) => {
    const currentLang = router.currentRoute.value.params.lang;
    // 先取原始 query.redirect 用于判定「URL 是否真有 redirect 参数」，
    // 缺失时再用默认 /:lang/upload 兜底（仅用于导航与分类，不改变 redirectPresent 语义）。
    const queryRedirect = router.currentRoute.value.query.redirect;
    const redirect = queryRedirect || `/${currentLang}/upload`;
    const resolvedRedirect = resolveRedirectTarget(redirect, {
      lang: currentLang,
    });
    const redirectTarget = resolvedRedirect.target;
    const redirectPresent = !!queryRedirect;

    // 手动 OAuth 进行中到达的 One Tap credential 直接忽略（方案 §13.2：一次页面生命周期最多一个认证完成流程）
    if (oauthInFlight) return;

    if (!response?.credential) {
      authAnalytics.trackAuthOauthCallbackCompleted({
        method: "google_one_tap",
        authFlow: "one_tap_login",
        actionStatus: "failed",
        reasonCode: "one_tap_credential_missing",
        sourcePosition,
        redirectTarget,
        redirectPresent,
      });
      return;
    }

    const credentialStart = performance.now();
    userStore.loginLoading = true;
    userStore.loginError = null;

    try {
      const { data, error } = await supabase.auth.signInWithIdToken({
        provider: "google",
        token: response.credential,
        nonce: googleOneTapRawNonce,
      });

      if (error) throw error;
      if (!data?.session) throw new Error("Invalid auth session");

      // 登录已成功：标记后，因成功跳转导致的卸载不再调用 cancel()，
      // 避免浮层随 credential_returned 已自动关闭时仍被代码关闭、回发多余的 cancel_called 埋点。
      loginSucceeded.value = true;

      authAnalytics.trackAuthOauthCallbackCompleted({
        method: "google_one_tap",
        authFlow: "one_tap_login",
        actionStatus: "success",
        sessionSource: "one_tap_id_token",
        sourcePosition,
        redirectTarget,
        redirectPresent,
        durationMs: Math.round(performance.now() - credentialStart),
      });

      // 组件已卸载则不再导航 / 触发 billing，避免 stale promise 写已销毁页面（方案 §13.6）
      if (isUnmounted.value) return;

      await completePostAuthSession({
        session: data.session,
        redirect: resolvedRedirect.path,
        redirectTarget,
        redirectPresent,
        authFlow: "one_tap_login",
        method: "google_one_tap",
        sourcePosition,
        sessionSource: "one_tap_id_token",
      });
    } catch (error) {
      authAnalytics.trackAuthOauthCallbackCompleted({
        method: "google_one_tap",
        authFlow: "one_tap_login",
        actionStatus: "failed",
        reasonCode: "one_tap_sign_in_failed",
        errorCode: error?.code || error?.name,
        sourcePosition,
        redirectTarget,
        redirectPresent,
        durationMs: Math.round(performance.now() - credentialStart),
      });
      userStore.loginError = "Authentication failed. Please try again.";
    } finally {
      userStore.loginLoading = false;
    }
  };

  const trackOneTapPromptMoment = (notification) => {
    // 用户选凭据的那一刻（credential_returned）：发出独立「one tap 被选」事件，
    // 仅表示选凭据动作，不含成功/失败（成功/失败由 auth_oauth_callback_completed 承载），不再 suppress。
    if (
      notification?.isDismissedMoment?.() &&
      notification.getDismissedReason?.() === "credential_returned"
    ) {
      authAnalytics.trackAuthOneTapSelected({ sourcePosition });
      return;
    }

    if (notification?.isDisplayed?.()) {
      authAnalytics.trackAuthOneTapPromptCompleted({
        actionStatus: "success",
        reasonCode: "displayed",
        sourcePosition,
      });
      return;
    }

    const reasonCode = notification?.isNotDisplayed?.()
      ? notification.getNotDisplayedReason?.() || "not_displayed"
      : notification?.isSkippedMoment?.()
      ? notification.getSkippedReason?.() || "skipped"
      : notification?.isDismissedMoment?.()
      ? notification.getDismissedReason?.() || "dismissed"
      : notification?.isDisplayMoment?.()
      ? "display_moment_not_displayed"
      : "unknown_prompt_moment";

    authAnalytics.trackAuthOneTapPromptCompleted({
      actionStatus: "skipped",
      reasonCode,
      sourcePosition,
    });
  };

  const initializeGoogleOneTap = async () => {
    if (oneTapInitialized || userStore.isLoggedIn) return;
    if (extensionRuntime.platform && extensionRuntime.platform !== "web") return;
    if (typeof window === "undefined" || typeof document === "undefined") return;

    const clientId = authRuntime.googleClientId;
    if (!clientId) {
      authAnalytics.trackAuthOneTapPromptCompleted?.({
        actionStatus: "skipped",
        reasonCode: "google_client_id_missing",
        sourcePosition,
      });
      if (runtimeEnv.isDevelopment) {
        console.warn(
          "[auth] VUE_APP_GOOGLE_CLIENT_ID is not configured; Google One Tap skipped"
        );
      }
      return;
    }

    authAnalytics.trackAuthOauthStarted({
      method: "google_one_tap",
      authFlow: "one_tap_login",
      elementName: "google_one_tap_prompt",
      sourcePosition,
    });

    try {
      const googleAccountsId = await loadGoogleGsiSdk();
      if (!googleAccountsId)
        throw new Error("Google Identity Services unavailable");

      googleOneTapRawNonce = createGoogleOneTapRawNonce();
      const googleOneTapHashedNonce = await createSha256Hex(
        googleOneTapRawNonce
      );
      googleAccountsId.initialize({
        client_id: clientId,
        callback: handleGoogleOneTapCredential,
        cancel_on_tap_outside: true,
        use_fedcm_for_prompt: true,
        nonce: googleOneTapHashedNonce,
      });
      oneTapInitialized = true;
      googleAccountsId.prompt(trackOneTapPromptMoment);
    } catch (error) {
      authAnalytics.trackAuthOneTapPromptCompleted?.({
        actionStatus: "failed",
        reasonCode: "gsi_script_load_failed",
        errorCode: error?.code || error?.name,
        sourcePosition,
      });
      if (runtimeEnv.isDevelopment) {
        console.warn("[auth] Google One Tap initialization failed", error);
      }
    }
  };

  onUnmounted(() => {
    isUnmounted.value = true;
    // 登录已成功则浮层随 credential_returned 已自动关闭，无需再 cancel（避免多余的 cancel_called 埋点）；
    // 仅当未成功登录（用户导航离开 / 失败）才显式关闭浮层。
    if (
      !loginSucceeded.value &&
      typeof window !== "undefined" &&
      window.google?.accounts?.id?.cancel
    ) {
      window.google.accounts.id.cancel();
    }
  });

  return {
    initializeGoogleOneTap,
    cancelOneTap: () => {
      if (typeof window !== "undefined") {
        window.google?.accounts?.id?.cancel?.();
      }
    },
    setOauthInFlight: (value) => {
      oauthInFlight = value;
    },
    getOauthInFlight: () => oauthInFlight,
  };
}
